import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { format, subDays } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Switch } from '../components/ui/switch'
import { Progress } from '../components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { toast } from 'sonner'
import { cn } from '../lib/utils'
import { useLanguage } from '../context/LanguageContext'
import { 
  TrendingUp, TrendingDown, Wallet, PiggyBank, AlertTriangle, 
  Download, Calendar, ArrowUpCircle, ArrowDownCircle, Target,
  Heart, Zap, DollarSign, AlertCircle, CheckCircle, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, Info, Lightbulb, Check, X, Receipt
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, Legend
} from 'recharts'
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

const getDateRange = (days) => ({
  start: format(subDays(new Date(), days), 'yyyy-MM-dd'),
  end: format(new Date(), 'yyyy-MM-dd')
})

export default function AnalisisFinancieroPage() {
  const { t, locale } = useLanguage()
  const dateLocale = locale === 'es-MX' ? es : enUS
  
  const quickRanges = [
    { days: 7, label: t('financialAnalysis.days7') },
    { days: 14, label: t('financialAnalysis.days14') },
    { days: 30, label: t('financialAnalysis.days30') },
    { days: 90, label: t('financialAnalysis.months3') },
  ]
  
  const [dateRange, setDateRange] = useState(getDateRange(30))
  const [includeInversiones, setIncludeInversiones] = useState(true)
  const [includeDesperdicio, setIncludeDesperdicio] = useState(true)
  const [analysis, setAnalysis] = useState(null)
  const [loading, setLoading] = useState(true)
  const [budgetStats, setBudgetStats] = useState(null)
  const [loadingBudgets, setLoadingBudgets] = useState(true)
  
  // Yearly chart state
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [yearlyData, setYearlyData] = useState(null)
  const [loadingYearly, setLoadingYearly] = useState(true)
  
  // Health score expansion state
  const [healthExpanded, setHealthExpanded] = useState(false)
  
  // Categories expansion state
  const [categoriesExpanded, setCategoriesExpanded] = useState(false)
  const MAX_COLLAPSED_CATEGORIES = 7

  const fetchAnalysis = useCallback(async () => {
    setLoading(true)
    try {
      const response = await axios.get(
        `${API}/finance/analysis?start_date=${dateRange.start}&end_date=${dateRange.end}&include_inversiones=${includeInversiones}&include_desperdicio=${includeDesperdicio}`
      )
      setAnalysis(response.data)
    } catch (error) {
      console.error('Error fetching analysis:', error)
      toast.error('Error al cargar análisis')
    } finally {
      setLoading(false)
    }
  }, [dateRange, includeInversiones, includeDesperdicio])

  const fetchBudgetStats = useCallback(async () => {
    setLoadingBudgets(true)
    try {
      const response = await axios.get(`${API}/finance/budgets/stats`)
      setBudgetStats(response.data)
    } catch (error) {
      console.error('Error fetching budget stats:', error)
    } finally {
      setLoadingBudgets(false)
    }
  }, [])
  
  const fetchYearlyData = useCallback(async () => {
    setLoadingYearly(true)
    try {
      const response = await axios.get(
        `${API}/finance/analysis/yearly?year=${selectedYear}&include_inversiones=${includeInversiones}&include_desperdicio=${includeDesperdicio}`
      )
      setYearlyData(response.data)
    } catch (error) {
      console.error('Error fetching yearly data:', error)
      toast.error(locale === 'es-MX' ? 'Error al cargar datos anuales' : 'Error loading yearly data')
    } finally {
      setLoadingYearly(false)
    }
  }, [selectedYear, includeInversiones, includeDesperdicio, locale])

  useEffect(() => {
    fetchAnalysis()
  }, [fetchAnalysis])

  useEffect(() => {
    fetchBudgetStats()
  }, [fetchBudgetStats])
  
  useEffect(() => {
    fetchYearlyData()
  }, [fetchYearlyData])
  
  // Generate available years (current year and 5 years back)
  const availableYears = []
  for (let y = currentYear; y >= currentYear - 5; y--) {
    availableYears.push(y)
  }

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API}/finance/export?start_date=${dateRange.start}&end_date=${dateRange.end}`
      )
      
      // Create download
      const blob = new Blob([response.data.csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = response.data.filename
      a.click()
      window.URL.revokeObjectURL(url)
      
      toast.success('CSV exportado')
    } catch (error) {
      toast.error('Error al exportar')
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale === 'es-MX' ? 'es-MX' : 'en-US', {
      style: 'currency',
      currency: locale === 'es-MX' ? 'MXN' : 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const getHealthScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-500'
    if (score >= 40) return 'text-amber-500'
    return 'text-rose-500'
  }

  const getHealthScoreLabel = (score) => {
    if (score >= 70) return t('financialAnalysis.excellent')
    if (score >= 40) return t('financialAnalysis.good')
    return t('financialAnalysis.needsImprovement')
  }

  if (loading && !analysis) {
    return (
      <div className="p-4 md:p-8 pb-safe flex items-center justify-center min-h-[50vh]">
        <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  const kpis = analysis?.kpis || {}
  const categoryData = analysis?.category_breakdown || []
  const healthBreakdown = analysis?.health_breakdown || null

  return (
    <div className="p-4 md:p-8 pb-safe max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold mb-1">{t('financialAnalysis.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('financialAnalysis.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          className="rounded-full"
          data-testid="export-btn"
        >
          <Download className="w-4 h-4 mr-2" /> {t('financialAnalysis.exportCSV')}
        </Button>
      </div>

      {/* Date Range Selector */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2 mb-4">
            {quickRanges.map((range) => (
              <Button
                key={range.days}
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() => setDateRange(getDateRange(range.days))}
              >
                {range.label}
              </Button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('financialAnalysis.from')}</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="h-11 w-full"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium">{t('financialAnalysis.to')}</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="h-11 w-full"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={includeInversiones}
                onCheckedChange={setIncludeInversiones}
              />
              <Label className="text-sm">{t('financialAnalysis.includeInvestments')}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={includeDesperdicio}
                onCheckedChange={setIncludeDesperdicio}
              />
              <Label className="text-sm">{t('financialAnalysis.includeWaste')}</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Financial Health Score - Expanded */}
      <Card className="mb-6 overflow-hidden" data-testid="health-score-card">
        <Collapsible open={healthExpanded} onOpenChange={setHealthExpanded}>
          <CardContent className="p-6">
            {/* Header with score */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-rose-500" />
                <span className="font-medium">{t('financialAnalysis.healthScore')}</span>
                <TooltipProvider>
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <button className="text-muted-foreground hover:text-foreground transition-colors">
                        <Info className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-sm">
                        {locale === 'es-MX' 
                          ? 'Este score se calcula con: ahorro, gastos hormiga, inversión, balance y disciplina de registro. Toca para ver el desglose completo.'
                          : 'This score is calculated with: savings, ant expenses, investment, balance and tracking discipline. Tap to see full breakdown.'}
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-3">
                <span className={cn("text-3xl font-bold", getHealthScoreColor(healthBreakdown?.score || kpis.health_score || 0))}>
                  {healthBreakdown?.score || kpis.health_score || 0}
                </span>
                <span className="text-sm text-muted-foreground">/100</span>
              </div>
            </div>
            
            <Progress 
              value={Math.max(0, Math.min(100, healthBreakdown?.score || kpis.health_score || 0))} 
              className="h-3 mb-3"
            />
            
            {/* Status label and expand button */}
            <CollapsibleTrigger asChild>
              <button 
                className="w-full flex items-center justify-between hover:bg-secondary/50 -mx-2 px-2 py-2 rounded-lg transition-colors"
                data-testid="health-expand-btn"
              >
                <span className={cn("text-sm font-medium", getHealthScoreColor(healthBreakdown?.score || kpis.health_score || 0))}>
                  {healthBreakdown?.status_label || getHealthScoreLabel(healthBreakdown?.score || kpis.health_score || 0)}
                </span>
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <span>{locale === 'es-MX' ? '¿Por qué este score?' : 'Why this score?'}</span>
                  {healthExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </button>
            </CollapsibleTrigger>
            
            {/* Expandable breakdown */}
            <CollapsibleContent className="mt-4 space-y-6">
              {healthBreakdown && (
                <>
                  {/* Factors breakdown */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                      {locale === 'es-MX' ? 'Desglose de factores' : 'Score breakdown'}
                    </h4>
                    
                    {healthBreakdown.factors.map((factor) => (
                      <div key={factor.id} className="space-y-2" data-testid={`factor-${factor.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {factor.status === 'good' && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                            {factor.status === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                            {factor.status === 'critical' && <AlertCircle className="w-4 h-4 text-rose-500" />}
                            <span className="text-sm font-medium">{factor.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              "text-sm font-bold",
                              factor.status === 'good' ? 'text-emerald-500' : 
                              factor.status === 'warning' ? 'text-amber-500' : 'text-rose-500'
                            )}>
                              +{factor.points}
                            </span>
                            <span className="text-xs text-muted-foreground">/{factor.max_points} pts</span>
                          </div>
                        </div>
                        
                        {/* Progress bar for factor */}
                        <div className="h-2 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className={cn(
                              "h-full rounded-full transition-all",
                              factor.status === 'good' ? 'bg-emerald-500' : 
                              factor.status === 'warning' ? 'bg-amber-500' : 'bg-rose-500'
                            )}
                            style={{ width: `${(factor.points / factor.max_points) * 100}%` }}
                          />
                        </div>
                        
                        {/* Current vs target */}
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>
                            {locale === 'es-MX' ? 'Actual' : 'Current'}: {
                              factor.unit === '$' 
                                ? formatCurrency(factor.current_value)
                                : `${factor.current_value}${factor.unit}`
                            }
                          </span>
                          <span>
                            {locale === 'es-MX' ? 'Objetivo' : 'Target'}: {
                              factor.target_comparison === 'less_than' ? '<' : '≥'
                            }{factor.unit === '$' 
                                ? formatCurrency(factor.target_value)
                                : `${factor.target_value}${factor.unit}`
                            }
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Recommendations */}
                  {healthBreakdown.recommendations.length > 0 && (
                    <div className="space-y-3 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-amber-500" />
                        <h4 className="text-sm font-semibold">
                          {locale === 'es-MX' ? 'Qué mejorar este mes' : 'What to improve this month'}
                        </h4>
                      </div>
                      
                      <div className="space-y-2">
                        {healthBreakdown.recommendations.slice(0, 5).map((rec, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50"
                            data-testid={`recommendation-${idx}`}
                          >
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm">{rec.message}</p>
                            </div>
                            <span className="flex-shrink-0 text-xs font-medium text-emerald-600 bg-emerald-500/10 px-2 py-1 rounded-full">
                              +{rec.potential_pts} pts
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Score explanation */}
                  <div className="pt-4 border-t">
                    <p className="text-xs text-muted-foreground text-center">
                      {locale === 'es-MX' 
                        ? `Score basado en el periodo ${dateRange.start} a ${dateRange.end}`
                        : `Score based on period ${dateRange.start} to ${dateRange.end}`}
                    </p>
                  </div>
                </>
              )}
            </CollapsibleContent>
          </CardContent>
        </Collapsible>
      </Card>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <ArrowUpCircle className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground">{t('financialAnalysis.income')}</span>
            </div>
            <p className="text-lg font-bold text-emerald-500">{formatCurrency(kpis.total_income || 0)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <ArrowDownCircle className="w-4 h-4 text-rose-500" />
              <span className="text-xs text-muted-foreground">{t('financialAnalysis.expenses')}</span>
            </div>
            <p className="text-lg font-bold text-rose-500">{formatCurrency(kpis.total_expense || 0)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-4 h-4 text-sky-500" />
              <span className="text-xs text-muted-foreground">{t('financialAnalysis.netFlow')}</span>
            </div>
            <p className={cn(
              "text-lg font-bold",
              (kpis.net_flow || 0) >= 0 ? 'text-sky-500' : 'text-amber-500'
            )}>
              {formatCurrency(kpis.net_flow || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <PiggyBank className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground">{t('financialAnalysis.savingsRate')}</span>
            </div>
            <p className={cn(
              "text-lg font-bold",
              (kpis.ahorro_ratio || 0) >= 20 ? 'text-emerald-500' : 'text-amber-500'
            )}>
              {(kpis.ahorro_ratio || 0).toFixed(1)}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-500 flex-shrink-0" />
              <span className="text-xs text-muted-foreground truncate">{t('financialAnalysis.investment')}</span>
            </div>
            <p className="text-sm font-bold">{formatCurrency(kpis.total_inversion || 0)}</p>
            <p className="text-xs text-muted-foreground">{(kpis.inversion_ratio || 0).toFixed(1)}% {t('financialAnalysis.ofIncome')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0" />
              <span className="text-xs text-muted-foreground truncate">{t('financialAnalysis.waste')}</span>
            </div>
            <p className="text-sm font-bold">{formatCurrency(kpis.total_desperdicio || 0)}</p>
            <p className="text-xs text-muted-foreground">{(kpis.desperdicio_ratio || 0).toFixed(1)}% {t('financialAnalysis.ofExpenses')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="w-4 h-4 text-sky-500 flex-shrink-0" />
              <span className="text-xs text-muted-foreground truncate">{t('financialAnalysis.transactions')}</span>
            </div>
            <p className="text-lg font-bold">{analysis?.transaction_count || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Budget Overview */}
      {budgetStats && budgetStats.categorias.length > 0 && (
        <Card className="mb-6" data-testid="budget-overview">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-4 h-4" />
                {t('financialAnalysis.monthlyBudgets')}
              </CardTitle>
              <div className="text-right">
                <p className="text-sm font-bold">
                  {formatCurrency(budgetStats.gastado_global)} / {formatCurrency(budgetStats.presupuesto_global)}
                </p>
                <p className={cn(
                  "text-xs font-medium",
                  budgetStats.porcentaje_global >= 100 ? 'text-red-500' :
                  budgetStats.porcentaje_global >= 80 ? 'text-yellow-500' :
                  'text-emerald-500'
                )}>
                  {budgetStats.porcentaje_global}% {locale === 'es-MX' ? 'del total' : 'of total'}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Global progress bar */}
            <div className="mb-4">
              <div className="h-3 bg-secondary rounded-full overflow-hidden">
                <div 
                  className={cn(
                    "h-full rounded-full transition-all",
                    budgetStats.porcentaje_global >= 100 ? 'bg-red-500' :
                    budgetStats.porcentaje_global >= 80 ? 'bg-yellow-500' :
                    'bg-emerald-500'
                  )}
                  style={{ width: `${Math.min(budgetStats.porcentaje_global, 100)}%` }}
                />
              </div>
            </div>

            {/* Category budgets */}
            <div className="space-y-3">
              {budgetStats.categorias.map((budget) => (
                <div key={budget.id} className="space-y-1" data-testid={`budget-item-${budget.id}`}>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {budget.porcentaje_usado >= 100 ? (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      ) : budget.porcentaje_usado >= 80 ? (
                        <AlertTriangle className="w-4 h-4 text-yellow-500" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                      )}
                      <span className="font-medium">{budget.categoria_nombre}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-muted-foreground">
                        {formatCurrency(budget.gastado)} / {formatCurrency(budget.monto_mensual)}
                      </span>
                      <span className={cn(
                        "ml-2 font-medium",
                        budget.porcentaje_usado >= 100 ? 'text-red-500' :
                        budget.porcentaje_usado >= 80 ? 'text-yellow-500' :
                        'text-emerald-500'
                      )}>
                        {budget.porcentaje_usado}%
                      </span>
                    </div>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className={cn(
                        "h-full rounded-full transition-all",
                        budget.porcentaje_usado >= 100 ? 'bg-red-500' :
                        budget.porcentaje_usado >= 80 ? 'bg-yellow-500' :
                        'bg-emerald-500'
                      )}
                      style={{ width: `${Math.min(budget.porcentaje_usado, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Alerts */}
            {budgetStats.alertas_pendientes.length > 0 && (
              <div className="mt-4 space-y-2">
                {budgetStats.alertas_pendientes.map((alerta, idx) => (
                  <div 
                    key={idx}
                    className={cn(
                      "flex items-center gap-2 p-3 rounded-lg text-sm",
                      alerta.tipo === 'danger' ? 'bg-red-500/10 text-red-500' : 'bg-yellow-500/10 text-yellow-500'
                    )}
                  >
                    {alerta.tipo === 'danger' ? (
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span>{alerta.mensaje}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Yearly Monthly Chart - Income vs Expense */}
      <Card className="mb-6" data-testid="yearly-chart">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {locale === 'es-MX' ? 'Ingresos vs Gastos por Mes' : 'Income vs Expenses by Month'}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSelectedYear(prev => prev - 1)}
                disabled={selectedYear <= currentYear - 5}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Select value={String(selectedYear)} onValueChange={(val) => setSelectedYear(Number(val))}>
                <SelectTrigger className="w-24 h-8" data-testid="year-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map((year) => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setSelectedYear(prev => prev + 1)}
                disabled={selectedYear >= currentYear}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {yearlyData && (
            <div className="flex flex-wrap gap-4 mt-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-emerald-500" />
                <span className="text-muted-foreground">
                  {locale === 'es-MX' ? 'Ingresos' : 'Income'}: {formatCurrency(yearlyData.total_income)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-rose-500" />
                <span className="text-muted-foreground">
                  {locale === 'es-MX' ? 'Gastos' : 'Expenses'}: {formatCurrency(yearlyData.total_expense)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "font-medium",
                  yearlyData.total_balance >= 0 ? 'text-emerald-500' : 'text-rose-500'
                )}>
                  {locale === 'es-MX' ? 'Balance' : 'Balance'}: {formatCurrency(yearlyData.total_balance)}
                </span>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="px-2 md:px-6">
          {loadingYearly ? (
            <div className="h-[280px] flex items-center justify-center">
              <div className="animate-pulse text-muted-foreground">{t('common.loading')}</div>
            </div>
          ) : yearlyData ? (
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={yearlyData.monthly_breakdown} 
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  barCategoryGap="15%"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis 
                    dataKey={locale === 'es-MX' ? 'month_name_es' : 'month_name_en'}
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 10 }}
                    stroke="hsl(var(--muted-foreground))"
                    width={55}
                    tickFormatter={(value) => {
                      if (value >= 1000000) return `$${(value/1000000).toFixed(1)}M`
                      if (value >= 1000) return `$${(value/1000).toFixed(0)}k`
                      return `$${value}`
                    }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload
                        return (
                          <div className="p-3 space-y-1">
                            <p className="font-medium">{locale === 'es-MX' ? data.month_name_es : data.month_name_en} {selectedYear}</p>
                            <p className="text-emerald-500 text-sm">
                              {locale === 'es-MX' ? 'Ingresos' : 'Income'}: {formatCurrency(data.income)}
                            </p>
                            <p className="text-rose-500 text-sm">
                              {locale === 'es-MX' ? 'Gastos' : 'Expenses'}: {formatCurrency(data.expense)}
                            </p>
                            <p className={cn(
                              "text-sm font-medium pt-1 border-t",
                              data.balance >= 0 ? 'text-emerald-600' : 'text-rose-600'
                            )}>
                              Balance: {formatCurrency(data.balance)}
                            </p>
                          </div>
                        )
                      }
                      return null
                    }}
                  />
                  <Bar 
                    dataKey="income" 
                    fill="#10b981" 
                    radius={[4, 4, 0, 0]} 
                    name={locale === 'es-MX' ? 'Ingresos' : 'Income'}
                    maxBarSize={40}
                  />
                  <Bar 
                    dataKey="expense" 
                    fill="#f43f5e" 
                    radius={[4, 4, 0, 0]} 
                    name={locale === 'es-MX' ? 'Gastos' : 'Expenses'}
                    maxBarSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-muted-foreground">
              {locale === 'es-MX' ? 'No hay datos disponibles' : 'No data available'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category Breakdown - Collapsible */}
      <Card data-testid="category-breakdown-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">{t('financialAnalysis.topCategoriesExpenses')}</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {locale === 'es-MX' ? 'Gráfica de barras por monto gastado' : 'Bar chart by amount spent'}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-2 md:px-6">
          {(() => {
            const expenseCategories = categoryData.filter(c => c.expense > 0).sort((a, b) => b.expense - a.expense)
            const totalCategories = expenseCategories.length
            const visibleCategories = categoriesExpanded 
              ? expenseCategories 
              : expenseCategories.slice(0, MAX_COLLAPSED_CATEGORIES)
            const hasMoreCategories = totalCategories > MAX_COLLAPSED_CATEGORIES
            
            if (expenseCategories.length === 0) {
              return (
                <div className="py-8 text-center text-muted-foreground">
                  {locale === 'es-MX' ? 'No hay gastos en este periodo' : 'No expenses in this period'}
                </div>
              )
            }
            
            return (
              <>
                <div 
                  style={{ height: Math.max(180, visibleCategories.length * 40) }} 
                  className="w-full transition-all duration-300"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={visibleCategories} 
                      layout="vertical" 
                      margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                      <XAxis 
                        type="number" 
                        tick={{ fontSize: 10 }} 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => {
                          if (value >= 1000000) return `$${(value/1000000).toFixed(1)}M`
                          if (value >= 1000) return `$${(value/1000).toFixed(0)}k`
                          return `$${value}`
                        }}
                      />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        tick={{ fontSize: 11 }}
                        stroke="hsl(var(--muted-foreground))"
                        width={90}
                        tickFormatter={(value) => value.length > 12 ? value.substring(0, 12) + '…' : value}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload
                            return (
                              <div className="p-3">
                                <p className="font-medium text-sm">{data.name}</p>
                                <p className="text-rose-500 text-sm mt-1">
                                  {locale === 'es-MX' ? 'Gastado' : 'Spent'}: {formatCurrency(data.expense)}
                                </p>
                              </div>
                            )
                          }
                          return null
                        }}
                      />
                      <Bar dataKey="expense" radius={[0, 4, 4, 0]} maxBarSize={28}>
                        {visibleCategories.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Expand/Collapse button */}
                {hasMoreCategories && (
                  <button
                    type="button"
                    onClick={() => setCategoriesExpanded(!categoriesExpanded)}
                    className="w-full flex items-center justify-center gap-2 mt-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 rounded-lg transition-colors"
                    data-testid="categories-expand-btn"
                  >
                    {categoriesExpanded ? (
                      <>
                        <span>{locale === 'es-MX' ? 'Ocultar' : 'Hide'}</span>
                        <ChevronUp className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        <span>{locale === 'es-MX' ? `Ver todas (${totalCategories})` : `View all (${totalCategories})`}</span>
                        <ChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </>
            )
          })()}
        </CardContent>
      </Card>
    </div>
  )
}
